import React, { createContext, useContext, useState, useCallback, useMemo, useRef, ReactNode } from 'react';
import { DogFormData, ActivityLevel, DietType, Pathology } from '../types';
import type { PetSnapshot } from '../types/pets';
import { loadDraft, saveDraft, clearDraft } from '../utils/localStorageDraft';
import { buildPetSnapshot } from '../utils/petSnapshot';

interface FormContextType {
  formData: Partial<DogFormData>;
  updateFormData: (data: Partial<DogFormData>) => void;
  resetForm: () => void;
  resetFunnel: () => void;
  currentStep: number;
  setCurrentStep: (step: number) => void;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  totalSteps: number;
  petsCompleted: PetSnapshot[];
  addPetSnapshot: (snapshot: PetSnapshot) => void;
  updatePetSnapshotData: (petId: string, data: Partial<DogFormData>) => void;
  removePetSnapshot: (id: string) => void;
  replaceCurrentWithPetSnapshot: (id: string, targetStep: number) => void;
  resetForNewPet: () => void;
  clearAllPets: () => void;
  restorePetForEditing: (petId: string, targetStep: number) => void;
  multiPetIntent: boolean;
  setMultiPetIntent: (v: boolean) => void;
}

const FormContext = createContext<FormContextType | undefined>(undefined);

const MAX_STEP = 14;
const DISPLAY_TOTAL_STEPS = 14;

const INITIAL_FORM_DATA: Partial<DogFormData> = {
  ageYears: 0,
  ageMonths: 0,
  ageTotalMonths: 0,
  peso: 0,
  activityLevel: ActivityLevel.MEDIO,
  pathologies: [Pathology.NINGUNA],
  dietType: DietType.COMPLETA,
  subscriptionStart: 'transition',
  selectedRecipes: [],
  acceptsMarketing: false,
};

function loadSavedState(): {
  formData: Partial<DogFormData>;
  step: number;
  petsCompleted: PetSnapshot[];
  multiPetIntent: boolean;
} | null {
  const draft = loadDraft();
  if (!draft || typeof draft.step !== 'number' || !draft.formData) return null;
  return {
    formData: { ...INITIAL_FORM_DATA, ...draft.formData },
    step: draft.step,
    petsCompleted: draft.petsCompleted ?? [],
    multiPetIntent: draft.multiPetIntent ?? false,
  };
}

export const FormProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [savedState] = useState(loadSavedState);
  const [formData, setFormData] = useState<Partial<DogFormData>>(savedState?.formData ?? INITIAL_FORM_DATA);
  const [currentStep, setCurrentStepRaw] = useState(savedState?.step ?? 1);
  const [petsCompleted, setPetsCompleted] = useState<PetSnapshot[]>(savedState?.petsCompleted ?? []);
  const [multiPetIntent, setMultiPetIntentRaw] = useState<boolean>(savedState?.multiPetIntent ?? false);

  const stepRef = useRef(currentStep);
  const petsRef = useRef(petsCompleted);
  const formDataRef = useRef(formData);
  const multiPetIntentRef = useRef(multiPetIntent);
  stepRef.current = currentStep;
  petsRef.current = petsCompleted;
  formDataRef.current = formData;
  multiPetIntentRef.current = multiPetIntent;

  const persist = useCallback((fd: Partial<DogFormData>, step: number, pets: PetSnapshot[], intent: boolean) => {
    saveDraft({ formData: fd, step, petsCompleted: pets, multiPetIntent: intent });
  }, []);

  const setCurrentStep = useCallback(
    (step: number) => {
      setCurrentStepRaw(step);
      persist(formDataRef.current, step, petsRef.current, multiPetIntentRef.current);
    },
    [persist],
  );

  const updateFormData = useCallback(
    (data: Partial<DogFormData>) => {
      setFormData((prev) => {
        const next = { ...prev, ...data };
        persist(next, stepRef.current, petsRef.current, multiPetIntentRef.current);
        return next;
      });
    },
    [persist],
  );

  const resetForm = useCallback(() => {
    setFormData(INITIAL_FORM_DATA);
    setCurrentStepRaw(1);
    clearDraft();
  }, []);

  const setMultiPetIntent = useCallback(
    (v: boolean) => {
      setMultiPetIntentRaw(v);
      multiPetIntentRef.current = v;
      persist(formDataRef.current, stepRef.current, petsRef.current, v);
    },
    [persist],
  );

  const resetFunnel = useCallback(() => {
    clearDraft();
    setPetsCompleted([]);
    petsRef.current = [];
    setMultiPetIntentRaw(false);
    multiPetIntentRef.current = false;
    setFormData(INITIAL_FORM_DATA);
    setCurrentStepRaw(1);
  }, []);

  const goToStep = useCallback(
    (step: number) => {
      setCurrentStepRaw(step);
      persist(formDataRef.current, step, petsRef.current, multiPetIntentRef.current);
    },
    [persist],
  );

  const goToNextStep = useCallback(() => {
    goToStep(Math.min(stepRef.current + 1, MAX_STEP));
  }, [goToStep]);

  const goToPreviousStep = useCallback(() => {
    goToStep(Math.max(stepRef.current - 1, 1));
  }, [goToStep]);

  const addPetSnapshot = useCallback(
    (snapshot: PetSnapshot) => {
      // Replace existing entry for the same pet id (covers re-save after edit and double-click).
      // Using id (not nombre) so two pets with the same name are treated as distinct.
      const existingIdx = petsRef.current.findIndex((p) => p.id === snapshot.id);
      const next =
        existingIdx >= 0
          ? petsRef.current.map((p, i) => (i === existingIdx ? snapshot : p))
          : [...petsRef.current, snapshot];
      petsRef.current = next;
      setPetsCompleted(next);
      persist(formDataRef.current, stepRef.current, next, multiPetIntentRef.current);
    },
    [persist],
  );

  const updatePetSnapshotData = useCallback(
    (petId: string, data: Partial<DogFormData>) => {
      const next = petsRef.current.map((pet) => {
        if (pet.id !== petId) return pet;
        const nextFormData = { ...pet.formData, ...data };
        return {
          ...pet,
          nombre: nextFormData.nombre || pet.nombre,
          raza: nextFormData.raza ?? pet.raza,
          peso: typeof nextFormData.peso === 'number' ? nextFormData.peso : pet.peso,
          formData: nextFormData,
        };
      });
      petsRef.current = next;
      setPetsCompleted(next);
      persist(formDataRef.current, stepRef.current, next, multiPetIntentRef.current);
    },
    [persist],
  );

  const resetForNewPet = useCallback(() => {
    setCurrentStepRaw(1);
    setFormData(INITIAL_FORM_DATA);
    persist(INITIAL_FORM_DATA, 1, petsRef.current, multiPetIntentRef.current);
  }, [persist]);

  const removePetSnapshot = useCallback(
    (id: string) => {
      const next = petsRef.current.filter((p) => p.id !== id);
      petsRef.current = next;
      setPetsCompleted(next);
      persist(formDataRef.current, stepRef.current, next, multiPetIntentRef.current);
    },
    [persist],
  );

  const replaceCurrentWithPetSnapshot = useCallback(
    (id: string, targetStep: number) => {
      const pet = petsRef.current.find((p) => p.id === id);
      if (!pet) return;
      const nextPets = petsRef.current.filter((p) => p.id !== id);
      const restoredData: Partial<DogFormData> = {
        ...INITIAL_FORM_DATA,
        ...(pet.formData as Partial<DogFormData>),
      };
      petsRef.current = nextPets;
      formDataRef.current = restoredData;
      stepRef.current = targetStep;
      setPetsCompleted(nextPets);
      setFormData(restoredData);
      setCurrentStepRaw(targetStep);
      persist(restoredData, targetStep, nextPets, multiPetIntentRef.current);
    },
    [persist],
  );

  const clearAllPets = useCallback(() => {
    petsRef.current = [];
    setPetsCompleted([]);
    persist(formDataRef.current, stepRef.current, [], multiPetIntentRef.current);
  }, [persist]);

  const restorePetForEditing = useCallback(
    (petId: string, targetStep: number) => {
      setPetsCompleted((prev) => {
        const pet = prev.find((p) => p.id === petId);
        if (!pet) return prev;

        // Save the currently active pet before replacing formData.
        // This prevents losing the other pet in multi-pet flows.
        const activeFd = formDataRef.current;
        const hasActivePet =
          !!activeFd.nombre &&
          !!activeFd.initiationPackDistribution &&
          Object.keys(activeFd.initiationPackDistribution).length > 0;
        const activeSnapshot = hasActivePet
          ? buildPetSnapshot(activeFd, {
              distribution: activeFd.initiationPackDistribution as Record<string, number>,
              discountedInitiationPrice: activeFd.initiationPackPrice ?? 0,
              monthlyPrice: activeFd.monthlyPlanPrice ?? 0,
              sizeChoice: (activeFd.sizeChoice ?? {}) as Record<string, string>,
            })
          : null;

        const withoutEdited = prev.filter((p) => p.id !== petId);
        const next = activeSnapshot ? [...withoutEdited, activeSnapshot] : withoutEdited;
        petsRef.current = next;
        const restoredData: Partial<DogFormData> = {
          ...INITIAL_FORM_DATA,
          ...(pet.formData as Partial<DogFormData>),
        };
        setFormData(() => {
          persist(restoredData, targetStep, next, multiPetIntentRef.current);
          return restoredData;
        });
        setCurrentStepRaw(targetStep);
        stepRef.current = targetStep;
        return next;
      });
    },
    [persist],
  );

  // useMemo: el value se recreaba en cada render del provider (objeto literal),
  // re-renderizando los 21 consumidores de useForm() en cada cambio. Las
  // funciones son useCallback-estables; solo los datos (formData, currentStep,
  // petsCompleted, multiPetIntent) deben disparar nueva identidad del value
  // (auditoría 2026-06-09). Coherente con AppConfigContext/AdminContext.
  const contextValue = useMemo(
    () => ({
      formData,
      updateFormData,
      resetForm,
      resetFunnel,
      currentStep,
      setCurrentStep,
      goToNextStep,
      goToPreviousStep,
      totalSteps: DISPLAY_TOTAL_STEPS,
      petsCompleted,
      addPetSnapshot,
      updatePetSnapshotData,
      removePetSnapshot,
      replaceCurrentWithPetSnapshot,
      resetForNewPet,
      clearAllPets,
      restorePetForEditing,
      multiPetIntent,
      setMultiPetIntent,
    }),
    [
      formData,
      currentStep,
      petsCompleted,
      multiPetIntent,
      updateFormData,
      resetForm,
      resetFunnel,
      setCurrentStep,
      goToNextStep,
      goToPreviousStep,
      addPetSnapshot,
      updatePetSnapshotData,
      removePetSnapshot,
      replaceCurrentWithPetSnapshot,
      resetForNewPet,
      clearAllPets,
      restorePetForEditing,
      setMultiPetIntent,
    ],
  );

  return <FormContext.Provider value={contextValue}>{children}</FormContext.Provider>;
};

export function useForm(): FormContextType {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error('useForm must be used within a FormProvider');
  }
  return context;
}
